import React, { useMemo } from "react";
import TableLoader from "../../shared/TableLoader";
import NoDataFound from "../../shared/NoDataFound";
import { formatTimestamp } from "./format";

// Bumped up from the shared tables' usual text-sm, per the "data-dense but
// readable" requirement, plus visible cell borders and zebra striping.
const thCls = "px-4 py-3 border border-gray-200 font-semibold whitespace-nowrap";
const tdCls = "px-4 py-3 border border-gray-200";

// Sort-direction indicator for a clickable header. Neutral (both arrows,
// dimmed) when this field isn't the active sort; a single solid arrow
// pointing the active direction when it is.
function SortArrow({ active, order }) {
  if (!active) {
    return <span className="inline-block ml-1 text-gray-300 text-xs align-middle">&#9650;&#9660;</span>;
  }
  return (
    <span className="inline-block ml-1 text-sky-700 text-xs align-middle">
      {order === "asc" ? <>&#9650;</> : <>&#9660;</>}
    </span>
  );
}

// Shared table for both tabs, both drill levels. Event-name columns are
// dynamic — the set of distinct keys actually present in `counts` across the
// current page — never a fixed list. Only the id column and the date column
// are sortable; clicking the already-active header flips its direction,
// clicking the other one switches to it (handled by the parent).
function IdCountsTable({
  items,
  idField,
  idHeader,
  showBaseUrl = false,
  startIndex = 0,
  onRowClick = null,
  onCountClick = null,
  loading = false,
  sort = null,
  order = "desc",
  onSortClick = null,
  customColumns = [],
  onRemoveCustomColumn = null,
  onCustomCountClick = null,
}) {
  const eventColumns = useMemo(() => {
    const set = new Set();
    (items || []).forEach((item) => {
      Object.keys(item.counts || {}).forEach((k) => set.add(k));
    });
    return Array.from(set).sort();
  }, [items]);

  const colCount = 2 + (showBaseUrl ? 1 : 0) + eventColumns.length + customColumns.length + 1;
  const rowClickable = typeof onRowClick === "function";
  const countClickable = typeof onCountClick === "function";
  const customCountClickable = typeof onCustomCountClick === "function";
  const sortable = typeof onSortClick === "function";

  return (
    <div className="overflow-x-auto bg-white shadow rounded-lg">
      <table className="min-w-full text-base text-gray-800 border-collapse">
        <thead className="bg-gray-50 text-sm uppercase text-gray-600">
          <tr>
            <th className={`${thCls} text-right w-12`}>#</th>
            <th
              className={`${thCls} text-left ${
                sortable
                  ? `cursor-pointer select-none transition-colors ${
                      sort === "name" ? "bg-sky-100 text-sky-700" : "hover:bg-gray-100"
                    }`
                  : ""
              }`}
              onClick={sortable ? () => onSortClick("name") : undefined}
            >
              {idHeader}
              {sortable && <SortArrow active={sort === "name"} order={order} />}
            </th>
            {showBaseUrl && <th className={`${thCls} text-left`}>Base URL</th>}
            {eventColumns.map((ev) => (
              <th key={ev} className={`${thCls} text-right`}>
                {ev}
              </th>
            ))}
            {customColumns.map((term) => (
              <th key={term} className={`${thCls} text-right`}>
                <span className="inline-flex items-center gap-1.5">
                  {term}
                  <button
                    type="button"
                    title="Remove column"
                    onClick={() => onRemoveCustomColumn && onRemoveCustomColumn(term)}
                    className="flex items-center justify-center w-5 h-5 rounded-full bg-red-100 text-red-600 font-bold leading-none hover:bg-red-600 hover:text-white transition-colors"
                  >
                    &times;
                  </button>
                </span>
              </th>
            ))}
            <th
              className={`${thCls} text-left ${
                sortable
                  ? `cursor-pointer select-none transition-colors ${
                      sort === "date" ? "bg-sky-100 text-sky-700" : "hover:bg-gray-100"
                    }`
                  : ""
              }`}
              onClick={sortable ? () => onSortClick("date") : undefined}
            >
              Date
              {sortable && <SortArrow active={sort === "date"} order={order} />}
            </th>
          </tr>
        </thead>
        {loading ? (
          <TableLoader colspan={colCount} />
        ) : items && items.length ? (
          <tbody>
            {items.map((item, idx) => {
              const id = item[idField];
              return (
                <tr
                  key={`${id}-${idx}`}
                  className={`${idx % 2 === 0 ? "bg-white" : "bg-slate-50"} ${
                    rowClickable ? "cursor-pointer hover:bg-sky-50" : ""
                  }`}
                  onClick={rowClickable ? () => onRowClick(id) : undefined}
                >
                  <td className={`${tdCls} text-right text-gray-400`}>{startIndex + idx + 1}</td>
                  <td className={`${tdCls} font-medium ${rowClickable ? "text-blue-700" : "text-gray-800"}`}>{id}</td>
                  {showBaseUrl && (
                    <td className={tdCls}>
                      {item.base_url ? (
                        <a
                          href={item.base_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 hover:underline break-all"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {item.base_url}
                        </a>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  )}
                  {eventColumns.map((ev) => {
                    const count = item.counts ? item.counts[ev] : undefined;
                    const hasCount = count !== undefined && count !== null;
                    return (
                      <td key={ev} className={`${tdCls} text-right`}>
                        {hasCount ? (
                          <button
                            type="button"
                            className={
                              countClickable
                                ? "text-blue-600 hover:underline cursor-pointer"
                                : "text-gray-700 cursor-default"
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              if (countClickable) onCountClick(id, ev);
                            }}
                          >
                            {count}
                          </button>
                        ) : (
                          <span className="text-gray-300">0</span>
                        )}
                      </td>
                    );
                  })}
                  {customColumns.map((term) => {
                    const count = item.customCounts ? item.customCounts[term] : undefined;
                    const hasCount = typeof count === "number" && count > 0;
                    return (
                      <td key={term} className={`${tdCls} text-right`}>
                        {hasCount ? (
                          <button
                            type="button"
                            className={
                              customCountClickable
                                ? "text-blue-600 hover:underline cursor-pointer"
                                : "text-gray-700 cursor-default"
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              if (customCountClickable) onCustomCountClick(id, term);
                            }}
                          >
                            {count}
                          </button>
                        ) : (
                          <span className="text-gray-300">0</span>
                        )}
                      </td>
                    );
                  })}
                  <td className={`${tdCls} whitespace-nowrap`}>{formatTimestamp(item.last_seen)}</td>
                </tr>
              );
            })}
          </tbody>
        ) : (
          <NoDataFound colspan={colCount} />
        )}
      </table>
    </div>
  );
}

export default IdCountsTable;
